import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { userApi } from '@/api';
import { QUERY_KEY_CONST } from '@/constant';
import usePaginationSearchParams from '@/hook/tanstackQuery/usePaginationSearchParams';
import type { User, UserApi, UserRole } from '@/type';

export const useUserDetailQuery = (userID: User['id']) => {
  return useQuery({
    queryFn: () =>
      userApi.getDetail({ id: userID }).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.USER_KEYS.getDetail(userID),
    enabled: !!userID,
  });
};

export const useUserListQuery = (param?: { enabled?: boolean }) => {
  const [searchParams] = useSearchParams();

  const paginationSearchParams = usePaginationSearchParams('user');
  const role = searchParams.get('userRole');

  const listParams = {
    ...paginationSearchParams,
    role: role as UserRole,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  return useQuery({
    queryFn: () =>
      userApi.getList({ ...listParams }).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.USER_KEYS.getList(listParams),
    enabled: param?.enabled ?? true,
  });
};

export const useUserListInfiniteQuery = (
  param?: UserApi.GetList.Request & { enabled?: boolean },
) => {
  const listParams = {
    ...param,
  };

  Object.entries(listParams).forEach(([key, value]) => {
    if (value === null) delete listParams[key as keyof typeof listParams];
  });

  return useInfiniteQuery({
    queryKey: QUERY_KEY_CONST.USER_KEYS.getList(listParams),
    queryFn: ({ pageParam }) =>
      userApi.getList({ ...listParams, cursor: pageParam }),
    initialPageParam: '',
    enabled: param?.enabled ?? true,
    getNextPageParam: nextPage => nextPage.data.cursor,
    getPreviousPageParam: prevPage => prevPage.data.cursor,
  });
};

export const useUserCountQuery = (param?: {
  enabled?: boolean;
  role?: UserRole;
}) => {
  const enabled = param?.enabled ?? true;

  return useQuery({
    queryFn: () =>
      userApi.getCount({ ...param }).then(res => res.data.resultData),
    queryKey: QUERY_KEY_CONST.USER_KEYS.getCount({ ...param }),
    enabled,
  });
};

export const useUserUpdateMutation = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (param: UserApi.Update.Request) => {
      return userApi.update(param);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
    },
  });
};

export const useUserRemoveQuery = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userID: User['id']) => {
      return userApi.remove({ id: userID });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_KEYS.getList(),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEY_CONST.USER_GROUP_KEYS.getList(),
      });
    },
  });
};
